import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  TextField,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import { 
  Edit, 
  Delete, 
  Add, 
  PersonAdd,
  Search,
  Info,
  CheckCircle,
  Warning,
  CalendarToday,
  Category,
  Description,
  Update,
  Person,
  AdminPanelSettings,
  VpnKey,
  Settings,
  Business
} from '@mui/icons-material';

const SetPermission = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [addPermissionDialogOpen, setAddPermissionDialogOpen] = useState(false);
  const [editPermissionDialogOpen, setEditPermissionDialogOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Permission form state
  const [permissionForm, setPermissionForm] = useState({
    user: '',
    role: '',
  });

  // Available users
  const availableUsers = [
    'John Smith',
    'Sarah Johnson',
    'Michael Brown',
    'Emily Davis',
    'David Wilson',
    'Lisa Anderson',
    'Robert Taylor',
    'Jennifer White',
    'James Miller',
    'Maria Garcia',
  ];

  // Available roles
  const availableRoles = [
    'Administrator',
    'Manager',
    'Sales Executive',
    'HR Executive',
    'Finance Executive',
    'Customer Support',
    'Data Analyst',
    'Operations Executive',
  ];

  // Sample permission data
  const [permissionData, setPermissionData] = useState([
    {
      id: 1,
      user: 'John Smith',
      role: 'Administrator',
      assignedDate: '2024-01-15',
      status: 'Active',
      department: 'IT Development',
    },
    {
      id: 2,
      user: 'Sarah Johnson',
      role: 'Manager',
      assignedDate: '2024-02-20',
      status: 'Active',
      department: 'Sales & Marketing',
    },
    {
      id: 3,
      user: 'Michael Brown',
      role: 'Finance Executive',
      assignedDate: '2024-03-10',
      status: 'Active',
      department: 'Finance & Accounts',
    },
    {
      id: 4,
      user: 'Emily Davis',
      role: 'HR Executive',
      assignedDate: '2024-04-05',
      status: 'Active',
      department: 'Human Resources',
    },
    {
      id: 5,
      user: 'David Wilson',
      role: 'Operations Executive',
      assignedDate: '2024-05-12',
      status: 'Active',
      department: 'Operations',
    },
    {
      id: 6,
      user: 'Lisa Anderson',
      role: 'Data Analyst',
      assignedDate: '2024-06-18',
      status: 'Inactive',
      department: 'Data Analytics',
    },
    {
      id: 7,
      user: 'Robert Taylor',
      role: 'Sales Executive',
      assignedDate: '2024-07-22',
      status: 'Active',
      department: 'Sales & Marketing',
    },
    {
      id: 8,
      user: 'Jennifer White',
      role: 'Customer Support',
      assignedDate: '2024-08-14',
      status: 'Active',
      department: 'Customer Service',
    },
  ]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleAddPermission = () => {
    setPermissionForm({ user: '', role: '' });
    setAddPermissionDialogOpen(true);
  };

  const handleEditPermission = (permission) => {
    setSelectedPermission(permission);
    setPermissionForm({
      user: permission.user,
      role: permission.role,
    });
    setEditPermissionDialogOpen(true);
  };

  const handleDeletePermission = (permissionId) => {
    if (window.confirm('Are you sure you want to delete this permission assignment?')) {
      setPermissionData(prev => prev.filter(item => item.id !== permissionId));
      console.log('Deleting permission:', permissionId);
    }
  };

  const handlePermissionFormChange = (field, value) => {
    setPermissionForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSavePermission = () => {
    if (!permissionForm.user.trim() || !permissionForm.role.trim()) {
      alert('Please select both user and role');
      return;
    }

    // Check if user already has a role assigned
    const existingAssignment = permissionData.find(item => item.user === permissionForm.user);
    if (existingAssignment) {
      alert('This user already has a role assigned. Please edit the existing assignment.');
      return;
    }

    const newPermission = {
      id: Math.max(...permissionData.map(item => item.id)) + 1,
      user: permissionForm.user,
      role: permissionForm.role,
      assignedDate: new Date().toISOString().split('T')[0],
      status: 'Active',
      department: 'General',
    };

    setPermissionData(prev => [...prev, newPermission]);
    setAddPermissionDialogOpen(false);
    setSuccessMessage('Permission assigned successfully!');
  };

  const handleUpdatePermission = () => {
    if (!permissionForm.user.trim() || !permissionForm.role.trim()) {
      alert('Please select both user and role');
      return;
    }

    setPermissionData(prev =>
      prev.map(item =>
        item.id === selectedPermission.id
          ? { 
              ...item, 
              user: permissionForm.user,
              role: permissionForm.role 
            }
          : item
      )
    );

    setEditPermissionDialogOpen(false);
    setSelectedPermission(null);
    setSuccessMessage('Permission updated successfully!');
  };

  const getFilteredPermissionData = () => {
    let filteredData = permissionData;

    // Filter by table search query
    if (tableSearchQuery.trim()) {
      filteredData = filteredData.filter(permission =>
        Object.values(permission).some(value =>
          String(value).toLowerCase().includes(tableSearchQuery.toLowerCase())
        )
      );
    }

    // Filter by tab selection
    if (tabValue === 1) {
      filteredData = filteredData.filter(permission => permission.status === 'Active');
    } else if (tabValue === 2) {
      filteredData = filteredData.filter(permission => permission.status === 'Inactive');
    }

    return filteredData;
  };

  const getFilteredByStatus = (status) => {
    return permissionData.filter(permission => permission.status === status);
  };

  const getSearchSummary = () => {
    const filteredData = getFilteredPermissionData();
    return `${filteredData.length} permission assignment(s) found`;
  };

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Active':
        return {
          backgroundColor: '#4caf50',
          color: 'white',
        };
      case 'Inactive':
        return {
          backgroundColor: '#f44336',
          color: 'white',
        };
      default:
        return {};
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      'Administrator': '#f44336',
      'Manager': '#2196f3',
      'Sales Executive': '#4caf50',
      'HR Executive': '#ff9800',
      'Finance Executive': '#9c27b0',
      'Customer Support': '#00bcd4',
      'Data Analyst': '#795548',
      'Operations Executive': '#607d8b',
    };
    return colors[role] || '#757575';
  };

  // Summary calculations
  const totalPermissions = permissionData.length;
  const activeCount = getFilteredByStatus('Active').length;
  const inactiveCount = getFilteredByStatus('Inactive').length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <PersonAdd sx={{ color: '#1976d2', fontSize: 32 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1976d2' }}>
          Set Permission Management
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 4, 
            borderRadius: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle sx={{ fontSize: 20 }} />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #90caf9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <VpnKey sx={{ color: '#1976d2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Total Permissions
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {totalPermissions}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e8f5e9', 
            border: '1px solid #c8e6c9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <CheckCircle sx={{ color: '#2e7d32', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Active Assignments
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {activeCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#ffebee', 
            border: '1px solid #ffcdd2',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Warning sx={{ color: '#d32f2f', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Inactive Assignments
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {inactiveCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Card */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <Settings sx={{ color: '#1976d2', fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                User Permission Management
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Search Permissions..."
                variant="outlined"
                size="small"
                value={tableSearchQuery}
                onChange={(e) => setTableSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 250 }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={handleAddPermission}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 2
                }}
              >
                Add Permission
              </Button>
            </Box>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Info sx={{ fontSize: 20 }} />
              {getSearchSummary()}
            </Box>
          </Alert>

          {/* Tab Navigation */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="permission status tabs">
              <Tab label="All Assignments" />
              <Tab label="Active" />
              <Tab label="Inactive" />
            </Tabs>
          </Box>
        
        {getFilteredPermissionData().length > 0 ? (
          <TableContainer sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>User Details</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Role Assignment</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Department</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Assigned Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredPermissionData()
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((permission, index) => (
                    <TableRow key={permission.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>{page * rowsPerPage + index + 1}</TableCell>
                      
                      {/* User Details */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Person sx={{ color: '#666', fontSize: 16 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                              {permission.user}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              User ID: {permission.id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Role Assignment */}
                      <TableCell>
                        <Chip
                          label={permission.role}
                          size="small"
                          sx={{
                            backgroundColor: getRoleColor(permission.role),
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '12px'
                          }}
                        />
                      </TableCell>

                      {/* Department */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Business sx={{ color: '#666', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                            {permission.department}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Assigned Date */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarToday sx={{ color: '#666', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                            📅 {permission.assignedDate}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={permission.status}
                          sx={{
                            ...getStatusStyle(permission.status),
                            fontWeight: 600,
                            fontSize: '12px'
                          }}
                          size="small"
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit Permission">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleEditPermission(permission)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Permission">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDeletePermission(permission.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={getFilteredPermissionData().length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        ) : (
          <Alert severity="warning" sx={{ mt: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Warning sx={{ fontSize: 20 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  No Permissions Found
                </Typography>
                <Typography variant="body2">
                  No permission assignments match your search criteria.
                </Typography>
              </Box>
            </Box>
          </Alert>
        )}
        </CardContent>
      </Card>

      {/* Add Permission Dialog */}
      <Dialog open={addPermissionDialogOpen} onClose={() => setAddPermissionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Add sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
              Add New Permission
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select User *</InputLabel>
                  <Select
                    value={permissionForm.user}
                    label="Select User *"
                    onChange={(e) => handlePermissionFormChange('user', e.target.value)}
                    required
                  >
                    {availableUsers.map((user) => (
                      <MenuItem key={user} value={user}>
                        {user}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select Role *</InputLabel>
                  <Select
                    value={permissionForm.role}
                    label="Select Role *"
                    onChange={(e) => handlePermissionFormChange('role', e.target.value)}
                    required
                  >
                    {availableRoles.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPermissionDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSavePermission} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Permission Dialog */}
      <Dialog open={editPermissionDialogOpen} onClose={() => setEditPermissionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Update sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
              Edit Permission
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select User *</InputLabel>
                  <Select
                    value={permissionForm.user}
                    label="Select User *"
                    onChange={(e) => handlePermissionFormChange('user', e.target.value)}
                    required
                  >
                    {availableUsers.map((user) => (
                      <MenuItem key={user} value={user}>
                        {user}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select Role *</InputLabel>
                  <Select
                    value={permissionForm.role}
                    label="Select Role *"
                    onChange={(e) => handlePermissionFormChange('role', e.target.value)}
                    required
                  >
                    {availableRoles.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPermissionDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleUpdatePermission} variant="contained" color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SetPermission;